import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PurchaseVendorSelector } from 'src/components/Selectors/PurchaseVendorSelector';
import { SellerAccountEmployeeSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { UserGroupSelector } from 'src/components/Selectors/UserGroupSelector';
import { TOTAL_VALUE_LENGTH_LIMIT } from 'src/components/TagsFormBody/TagValueCell';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosEnumSelect, PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import {
  BOOLEAN_OPTIONS_CONTENT,
  MARKET_PLACE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { ToMarketplaceEnum } from 'src/utils/tagUtils';
import { Marketplace, TagsValueType } from 'src/WebApiController';

import * as styles from './TagValueFilterInput.css';

const MAX_TAG_VALUES = 20;

export type TagInfoFilter = {
  tagName: string | null;
  tagValue: string | null;
};

export const TagValueFilterInput = ({
  value,
  values,
  valueType,
  onChange,
  onValuesChange,
  disabled,
  isMultiValue,
}: {
  disabled?: boolean;
  value: string;
  values?: string[];
  valueType?: TagsValueType | null;
  onChange: (newValue: string) => void;
  onValuesChange?: (newValues: string[]) => void;
  isMultiValue?: boolean;
}) => {
  const { timeZone, timezoneOffsetMins } = useSiteTimezoneContext();
  const [inputText, setInputText] = useState('');
  const inputTextRef = useRef('');
  const valuesRef = useRef<string[] | undefined>(values);
  const onValuesChangeRef = useRef(onValuesChange);

  useEffect(() => {
    setInputText('');
  }, [valueType, isMultiValue]);

  inputTextRef.current = inputText;
  valuesRef.current = values;
  onValuesChangeRef.current = onValuesChange;

  const isFreetextMultiValue =
    isMultiValue &&
    valueType !== TagsValueType.Date &&
    valueType !== TagsValueType.Marketplace &&
    valueType !== TagsValueType.Boolean &&
    valueType !== TagsValueType.POSUser &&
    valueType !== TagsValueType.UserGroup &&
    valueType !== TagsValueType.Vendor;

  const commitInputText = useCallback(() => {
    const raw = inputTextRef.current;
    if (raw.length === 0) return;

    const trimmed = raw.trim();
    // All-whitespace input → empty string (for matching empty tag values)
    const value = trimmed.length === 0 ? '' : trimmed;

    const current = valuesRef.current ?? [];
    if (current.length < MAX_TAG_VALUES && !current.includes(value)) {
      onValuesChangeRef.current?.([...current, value]);
    }
    setInputText('');
  }, []);

  return (
    <>
      {valueType === TagsValueType.Date ? (
        <Stack direction="column" gap="m">
          <DatePickerInput
            date={
              value
                ? new Date(
                    new Date(value).getTime() + timezoneOffsetMins * 60 * 1000
                  )
                : undefined
            }
            onDateChange={(date: Date) => {
              const newDate = value ? new Date(value) : new Date();
              newDate.setFullYear(date.getFullYear());
              newDate.setMonth(date.getMonth());
              newDate.setDate(date.getDate());

              // Setting to yyyy-MM-dd format to match ISO format
              onChange(formatInTimeZone(newDate, timeZone, 'yyyy-MM-dd'));
            }}
            disabled={disabled}
          />
        </Stack>
      ) : valueType === TagsValueType.Marketplace ? (
        <PosEnumSelect
          value={ToMarketplaceEnum(value)}
          onChange={(value) => {
            onChange(Marketplace[value as keyof typeof Marketplace]);
          }}
          disabled={disabled}
          valueOptionsContent={MARKET_PLACE_TO_CID}
        />
      ) : valueType === TagsValueType.Boolean ? (
        <PosSelect
          displayText={value}
          value={value}
          disabled={disabled}
          onChange={onChange}
          valueOptionsContent={BOOLEAN_OPTIONS_CONTENT}
          placeholderText={ContentId.Value}
        />
      ) : valueType === TagsValueType.POSUser ? (
        <SellerAccountEmployeeSelector
          value={value}
          onChange={onChange}
          placeholderText={ContentId.Value}
          disabled={disabled}
        />
      ) : valueType === TagsValueType.UserGroup ? (
        <UserGroupSelector
          value={value}
          onChange={onChange}
          placeholderText={ContentId.Value}
          disabled={disabled}
        />
      ) : valueType === TagsValueType.Vendor ? (
        <PurchaseVendorSelector
          value={value}
          onChange={onChange}
          placeholderText={ContentId.Value}
          disabled={disabled}
        />
      ) : isFreetextMultiValue ? (
        <div>
          {(values?.length ?? 0) > 0 && (
            <div className={styles.chipContainer}>
              {values?.map((v, i) => (
                <span key={i} className={styles.chip}>
                  {v === '' ? (
                    <span className={styles.emptyValueLabel}>(empty)</span>
                  ) : (
                    v
                  )}
                  <button
                    type="button"
                    aria-label={v === '' ? 'Remove empty value' : `Remove ${v}`}
                    className={styles.removeButton}
                    onClick={() =>
                      onValuesChangeRef.current?.(
                        values.filter((_, idx) => idx !== i)
                      )
                    }
                  >
                    &times;
                  </button>
                </span>
              ))}
              {values && values.length > 1 && (
                <button
                  type="button"
                  aria-label="Clear all values"
                  className={styles.clearAllButton}
                  onClick={() => {
                    onValuesChangeRef.current?.([]);
                    setInputText('');
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}
          <PosTextField
            className={styles.valueInput}
            rootProps={{ variant: 'small' }}
            disabled={disabled}
            value={inputText}
            type="text"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inputTextRef.current.length > 0) {
                e.preventDefault();
                commitInputText();
              }
            }}
            onBlur={commitInputText}
            onChange={(e) => {
              let newValue = e.target.value ?? '';
              const prev = inputTextRef.current;
              if (prev.endsWith(' ') && newValue === prev.slice(0, -1) + '. ') {
                newValue = prev + ' ';
              }
              setInputText(newValue);
            }}
            maxLength={TOTAL_VALUE_LENGTH_LIMIT}
          />
        </div>
      ) : (
        <PosTextField
          className={styles.valueInput}
          rootProps={{ variant: 'small' }}
          disabled={disabled}
          value={value}
          type={
            valueType === TagsValueType.Decimal ||
            valueType === TagsValueType.Int
              ? 'number'
              : 'text'
          }
          onChange={(e) => {
            const newValue = e.target.value ?? null;
            onChange(newValue);
          }}
          maxLength={TOTAL_VALUE_LENGTH_LIMIT}
        />
      )}
    </>
  );
};
